<template>
  <div class="register-form">
    <v-form @submit.prevent="handleRegister" ref="registerForm" v-model="formValid">
      <h2 class="text-h5 text-center mb-6 font-weight-medium">
        Create Account
      </h2>
      
      <v-row>
        <v-col cols="6">
          <v-text-field
            v-model="form.first_name"
            label="First Name"
            prepend-inner-icon="mdi-account"
            :rules="nameRules"
            required
            class="mb-3"
          />
        </v-col>
        <v-col cols="6">
          <v-text-field
            v-model="form.last_name"
            label="Last Name"
            :rules="nameRules"
            required
            class="mb-3"
          />
        </v-col>
      </v-row>
      
      <v-text-field
        v-model="form.username"
        label="Username"
        prepend-inner-icon="mdi-at"
        :rules="usernameRules"
        required
        class="mb-3"
      />
      
      <v-text-field
        v-model="form.email"
        label="Email Address"
        type="email"
        prepend-inner-icon="mdi-email"
        :rules="emailRules"
        required
        class="mb-3"
      />
      
      <v-text-field
        v-model="form.phone"
        label="Phone Number (Optional)"
        prepend-inner-icon="mdi-phone"
        class="mb-3"
      />
      
      <v-text-field
        v-model="form.password"
        label="Password"
        :type="showPassword ? 'text' : 'password'"
        prepend-inner-icon="mdi-lock"
        :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
        @click:append-inner="showPassword = !showPassword"
        :rules="passwordRules"
        required
        class="mb-3"
      />
      
      <v-text-field
        v-model="form.confirmPassword"
        label="Confirm Password"
        :type="showConfirmPassword ? 'text' : 'password'"
        prepend-inner-icon="mdi-lock-check"
        :append-inner-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
        @click:append-inner="showConfirmPassword = !showConfirmPassword"
        :rules="confirmPasswordRules"
        required
        class="mb-4"
      />
      
      <v-checkbox
        v-model="form.agreeTerms"
        :rules="[v => !!v || 'You must agree to the terms and conditions']"
        required
        class="mb-4"
      >
        <template #label>
          <span class="text-caption">
            I agree to the 
            <a href="#" class="text-primary text-decoration-none">
              Terms and Conditions
            </a>
          </span>
        </template>
      </v-checkbox>
      
      <v-btn
        type="submit"
        block
        size="large"
        :loading="loading"
        :disabled="!formValid"
        class="mb-4"
      >
        Create Account
      </v-btn>
      
      <v-divider class="my-6" />
      
      <div class="text-center">
        <span class="text-medium-emphasis">Already have an account? </span>
        <router-link
          to="/auth/login"
          class="text-decoration-none text-primary font-weight-medium"
        >
          Sign in
        </router-link>
      </div>
    </v-form>
  </div>
</template>

<script>
import { ref, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import { useToast } from 'vue-toastification'

export default {
  name: 'Register',
  setup() {
    const router = useRouter()
    const authStore = useAuthStore()
    const toast = useToast()
    
    const registerForm = ref(null)
    const formValid = ref(false)
    const showPassword = ref(false)
    const showConfirmPassword = ref(false)
    const loading = ref(false)
    
    const form = reactive({
      first_name: '',
      last_name: '',
      username: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      agreeTerms: false
    })
    
    const nameRules = [
      v => !!v || 'This field is required',
      v => v.length >= 2 || 'Must be at least 2 characters'
    ]
    
    const usernameRules = [
      v => !!v || 'Username is required',
      v => v.length >= 3 || 'Username must be at least 3 characters',
      v => /^[a-zA-Z0-9_]+$/.test(v) || 'Username can only contain letters, numbers, and underscores'
    ]
    
    const emailRules = [
      v => !!v || 'Email is required',
      v => /.+@.+\..+/.test(v) || 'Email must be valid'
    ]
    
    const passwordRules = [
      v => !!v || 'Password is required',
      v => v.length >= 6 || 'Password must be at least 6 characters',
      v => /(?=.*[a-z])/.test(v) || 'Password must contain at least one lowercase letter',
      v => /(?=.*[A-Z])/.test(v) || 'Password must contain at least one uppercase letter',
      v => /(?=.*\d)/.test(v) || 'Password must contain at least one number'
    ]
    
    const confirmPasswordRules = [
      v => !!v || 'Please confirm your password',
      v => v === form.password || 'Passwords do not match'
    ]
    
    const handleRegister = async () => {
      if (!formValid.value) return
      
      loading.value = true
      
      try {
        const userData = {
          first_name: form.first_name,
          last_name: form.last_name,
          username: form.username,
          email: form.email,
          phone: form.phone || undefined,
          password: form.password
        }
        
        const result = await authStore.register(userData)
        
        if (result.success) {
          toast.success('Account created successfully! Please sign in.')
          router.push('/auth/login')
        } else {
          toast.error(result.message)
        }
      } catch (error) {
        toast.error('An unexpected error occurred. Please try again.')
      } finally {
        loading.value = false
      }
    }
    
    return {
      registerForm,
      formValid,
      showPassword,
      showConfirmPassword,
      loading,
      form,
      nameRules,
      usernameRules,
      emailRules,
      passwordRules,
      confirmPasswordRules,
      handleRegister
    }
  }
}
</script>

<style scoped lang="scss">
.register-form {
  width: 100%;
}

.v-text-field {
  margin-bottom: 8px;
}

.v-btn {
  text-transform: none;
  font-weight: 500;
}

.text-primary {
  color: rgb(var(--v-theme-primary)) !important;
}

.text-medium-emphasis {
  opacity: 0.7;
}
</style>